import { MenuOptionType } from "@dexteel/mesf-core";
import {
	AddOutlined,
	AddTask,
	Delete,
	EditOutlined,
} from "@mui/icons-material";
import { Event } from "../models/Event";

export const useCalendarOptions = ({
	onNew,
	onEdit,
	onDelete,
	openConfirmModal,
	canEdit = true,
	canAdd = true,
}: {
	onNew: (
		startDate: Date,
		endDate?: Date | null,
		previousSection?: string,
		nextSection?: string,
	) => void;
	onEdit: (id: number) => void;
	onDelete: (id: number) => void;
	openConfirmModal?: (event: Event) => void;
	canEdit?: boolean;
	canAdd?: boolean;
}) => {
	const getMenuOptions = (eventData: any) => {
		let options: MenuOptionType[] = [];

		const eventId = eventData.EventId;
		const fromCalendar = eventData.fromCalendar;
		const isCompleted = eventData.StatusCode === "C";
		const isMatched = eventData.IsMatched;
		const isLastMatched = eventData.IsLastMatched;

		if (canEdit && eventId && isCompleted) {
			options = options.concat([
				{
					name: "Set as 'Confirmed'",
					key: "set_as_confirmed",
					onClick: () => {
						openConfirmModal ? openConfirmModal(eventData as Event) : null;
					},
					icon: <AddTask />,
				},
			]);
		}
		if (canAdd && eventData.isEmptySpace) {
			options = options.concat([
				{
					name: "New Event",
					key: "new_event",
					onClick: () => {
						if (fromCalendar) {
							onNew(eventData.clickedDate);
						} else {
							onNew(eventData.start, eventData.end);
						}
					},
					icon: <AddOutlined />,
				},
			]);
		}
		if (canAdd && eventData.SectionName && !fromCalendar) {
			const event = eventData as Event;
			options = options.concat([
				{
					name: `New Event Before '${event.SectionName}'`,
					key: "new_event_before",
					onClick: () => {
						const startWithForward = new Date(event.Start);
						startWithForward.setHours(event.Start.getHours() - 2);
						onNew(
							event.PreviousEnd ?? startWithForward,
							event.Start,
							event.PreviousSection ?? undefined,
							event.SectionName ?? undefined,
						);
					},
					icon: <AddOutlined />,
					disabled: isMatched,
				},
				{
					name: `New Event After '${event.SectionName}'`,
					key: "new_event_after",
					onClick: () => {
						const endWithDelay = new Date(event.End!);
						endWithDelay.setHours(event.End!.getHours() + 2);
						onNew(
							event.End ?? event.Start,
							event.NextStart ?? endWithDelay,
							event.SectionName ?? undefined,
							event.NextSection ?? undefined,
						);
					},
					icon: <AddOutlined />,
					disabled: isMatched && !isLastMatched,
				},
			]);
		}
		if (eventId) {
			options = options.concat([
				{
					name: "Edit Event",
					key: "edit_event",
					onClick: () => {
						onEdit(eventId);
					},
					disabled: isMatched || (eventData.EventTypeCode !== "D" && !canEdit),
					icon: <EditOutlined />,
				},
				{
					name: "Delete Event",
					key: "delete_event",
					onClick: () => {
						onDelete(eventId);
					},
					disabled: isMatched || (eventData.EventTypeCode !== "D" && !canEdit),
					icon: <Delete />,
				},
			]);
		}

		return options;
	};

	return { getMenuOptions };
};
